import {
	AuthenticationUser,
	useGuestQuoteLoginMutation,
	useRefreshTokenMutation,
	useRequestGuestQuoteAccessCodeMutation,
} from '@codezo/graphql';
import { AlertTriangle, ArrowLeft, ArrowRight, Loader2, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../hooks/useAuth';
import { formatLoginUser } from '../../lib/format-auth-user';
import { classifyGuestFailure } from '../../lib/guest-failure';
import { clearGuestSession, startGuestSession } from '../../lib/guest-session';

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const CODE_LENGTH = 6;
/** Backend rate-limits resends; keep the button quiet for a beat rather than letting it 429. */
const RESEND_COOLDOWN_SECONDS = 30;

interface GuestAuthChallengeProps {
	/** The opaque `?token=` from the emailed link. Both mutations require it. */
	linkToken: string;
	/** Shown above the form when an established session ended rather than a first visit. */
	expired?: boolean;
	/** Fires once the session is live and the user is in context. */
	onAuthenticated: (orderQuoteId: string) => void;
}

/**
 * The email → code challenge behind a guest acceptance link.
 *
 * Verifying sets a real organization refresh cookie, so this hydrates the auth
 * context the same way a normal login does and hands the caller a live session.
 * Rendered both as the landing page and inline when a guest's session lapses,
 * so a lapsed session never navigates the guest away from their quote.
 */
export function GuestAuthChallenge({
	linkToken,
	expired = false,
	onAuthenticated,
}: GuestAuthChallengeProps) {
	const { setUser, setAccessToken } = useAuth();

	const [step, setStep] = useState<'email' | 'code'>('email');
	const [email, setEmail] = useState('');
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	/** Set when the link itself is dead — no code will help, so the form goes away. */
	const [deadLink, setDeadLink] = useState<string | null>(null);
	const [cooldown, setCooldown] = useState(0);
	const [hydrating, setHydrating] = useState(false);

	const [requestCode, { loading: requesting }] = useRequestGuestQuoteAccessCodeMutation();
	const [guestLogin, { loading: loggingIn }] = useGuestQuoteLoginMutation();
	const [refreshToken] = useRefreshTokenMutation();

	useEffect(() => {
		if (cooldown <= 0) return;
		const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(timer);
	}, [cooldown]);

	const trimmedEmail = email.trim();
	const emailValid = EMAIL_RE.test(trimmedEmail);
	const codeValid = code.trim().length === CODE_LENGTH;
	const verifying = loggingIn || hydrating;

	/** The link is dead: drop the stale marker and switch to a terminal screen. */
	const killLink = (message?: string | null) => {
		clearGuestSession();
		setDeadLink(message || 'This link is no longer valid.');
	};

	const handleRequestCode = async (isResend = false) => {
		if (!emailValid || requesting) return;
		setError(null);
		try {
			const { data } = await requestCode({
				variables: { input: { token: linkToken, email: trimmedEmail } },
			});
			const res = data?.requestGuestQuoteAccessCode;
			if (res && res.success === false) {
				// A dead link can surface here too, if the backend ever stops
				// answering neutrally for revoked tokens.
				if (classifyGuestFailure(res.message, res.messageKey) === 'terminal') {
					killLink(res.message);
					return;
				}
				setError(res.message || 'Could not send a code. Try again.');
				return;
			}
			// The response is deliberately neutral — a wrong address looks exactly
			// like a right one, so always advance. A bad address simply never
			// yields a working code.
			setStep('code');
			setCooldown(RESEND_COOLDOWN_SECONDS);
			if (isResend) setCode('');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not send a code. Try again.');
		}
	};

	const handleVerify = async () => {
		if (!codeValid || verifying) return;
		setError(null);
		try {
			const { data } = await guestLogin({
				variables: { input: { token: linkToken, email: trimmedEmail, code: code.trim() } },
			});
			const res = data?.guestQuoteLogin;
			// Wrong or expired code, revoked link, cross-org address — all arrive
			// as `success: false` with a readable reason.
			if (!res?.success || !res.token || !res.orderQuoteId) {
				// A revoked link is final — don't offer the form again.
				if (classifyGuestFailure(res?.message) === 'terminal') {
					killLink(res?.message);
					return;
				}
				setError(res?.message || 'That code could not be verified.');
				return;
			}

			const orderQuoteId = res.orderQuoteId;
			startGuestSession(orderQuoteId, linkToken);
			setAccessToken(res.token);

			// The refresh cookie is set now; trade it for the user record so the
			// context is populated exactly as it would be after a normal login.
			setHydrating(true);
			const { data: refreshed } = await refreshToken();
			const refreshedUser = refreshed?.refreshToken?.user;
			if (!refreshedUser) {
				setHydrating(false);
				setError('Signed in, but your session could not be started. Try again.');
				return;
			}
			setUser(formatLoginUser(refreshedUser as AuthenticationUser));
			setAccessToken(refreshed.refreshToken.token);
			onAuthenticated(orderQuoteId);
		} catch (err) {
			setHydrating(false);
			setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
		}
	};

	// Dead link — a new code can't fix it, so no form and no retry.
	if (deadLink) {
		return (
			<>
				<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">
					This link is no longer valid
				</h1>
				<p className="mt-2 text-sm text-text-3">{deadLink}</p>
				<div className="mt-5 flex items-start gap-2 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5 text-sm text-text-4">
					<Mail size={14} className="mt-0.5 shrink-0" />
					<span>
						Ask whoever sent you the offer for a new link — they can issue one at any
						time.
					</span>
				</div>
			</>
		);
	}

	if (step === 'email') {
		return (
			<>
				<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">
					{expired ? 'Your session has ended' : 'Confirm your email'}
				</h1>
				<p className="mt-2 text-sm text-text-3">
					{expired
						? 'Confirm your email and we’ll send a new code to get you back in.'
						: 'Enter the email address this link was sent to. We’ll send you a one-time code — no account needed.'}
				</p>

				<div className="mt-6 flex flex-col gap-1.5">
					<Label>Email address</Label>
					<Input
						autoFocus
						type="email"
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							if (error) setError(null);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleRequestCode();
							}
						}}
						placeholder="name@company.com"
						disabled={requesting}
					/>
				</div>

				{error && (
					<div className="mt-3">
						<GuestNotice title={error} />
					</div>
				)}

				<Button
					className="mt-5 w-full"
					onClick={() => handleRequestCode()}
					disabled={!emailValid || requesting}
				>
					{requesting ? (
						<Loader2 size={14} data-icon="inline-start" className="animate-spin" />
					) : (
						<ArrowRight size={14} data-icon="inline-start" />
					)}
					{requesting ? 'Sending…' : 'Send me a code'}
				</Button>

				<GuestFootnote />
			</>
		);
	}

	return (
		<>
			<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">Enter your code</h1>
			<p className="mt-2 text-sm text-text-3">
				We sent a {CODE_LENGTH}-digit code to{' '}
				<span className="text-text-2">{trimmedEmail}</span>. It expires shortly.
			</p>

			<div className="mt-6 flex flex-col gap-1.5">
				<Label>Verification code</Label>
				<Input
					autoFocus
					inputMode="numeric"
					autoComplete="one-time-code"
					maxLength={CODE_LENGTH}
					value={code}
					onChange={(e) => {
						setCode(e.target.value.replace(/\D/g, ''));
						if (error) setError(null);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							handleVerify();
						}
					}}
					placeholder="123456"
					className="text-center tracking-[0.4em]"
					disabled={verifying}
				/>
			</div>

			{error && (
				<div className="mt-3">
					<GuestNotice title={error} detail="Codes expire — request a new one below." />
				</div>
			)}

			<Button
				className="mt-5 w-full"
				onClick={handleVerify}
				disabled={!codeValid || verifying}
			>
				{verifying ? (
					<Loader2 size={14} data-icon="inline-start" className="animate-spin" />
				) : (
					<ArrowRight size={14} data-icon="inline-start" />
				)}
				{verifying ? 'Verifying…' : 'View offer'}
			</Button>

			<div className="mt-4 flex items-center justify-between gap-3">
				<button
					onClick={() => {
						setStep('email');
						setCode('');
						setError(null);
					}}
					className="flex items-center gap-1 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
					disabled={verifying}
				>
					<ArrowLeft size={12} />
					Change email
				</button>
				<button
					onClick={() => handleRequestCode(true)}
					className="bg-transparent text-sm text-text-3 transition-colors hover:text-text-1 disabled:cursor-not-allowed disabled:text-text-4 disabled:hover:text-text-4"
					disabled={cooldown > 0 || requesting}
				>
					{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
				</button>
			</div>

			<GuestFootnote />
		</>
	);
}

/* ── Shared bits ── */

export function GuestNotice({ title, detail }: { title: string; detail?: string }) {
	return (
		<div className="flex items-start gap-3 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5">
			<span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-danger/10 text-status-danger">
				<AlertTriangle size={11} strokeWidth={2.75} />
			</span>
			<div className="min-w-0">
				<div className="text-sm font-medium text-text-1">{title}</div>
				{detail && <div className="mt-0.5 text-sm text-text-4">{detail}</div>}
			</div>
		</div>
	);
}

function GuestFootnote() {
	return (
		<div className="mt-5 flex items-start gap-2 text-sm text-text-4">
			<Mail size={14} className="mt-0.5 shrink-0" />
			<span>
				Only the address the link was sent to can open it — that&apos;s what the code
				checks. The sender can revoke a link at any time.
			</span>
		</div>
	);
}
